export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function apiV1Base(): string {
  // Runtime (подстановка в index.html через start.sh) > build-time (VITE_) > fallback
  const RUNTIME_PLACEHOLDER = '__API_BASE_URL__';
  const runtimeRaw =
    typeof window !== 'undefined' && window.VITE_API_BASE_URL && window.VITE_API_BASE_URL !== RUNTIME_PLACEHOLDER
      ? String(window.VITE_API_BASE_URL).trim()
      : '';
  const envRaw = typeof import.meta.env.VITE_API_BASE_URL === 'string' ? import.meta.env.VITE_API_BASE_URL.trim() : '';

  const resolved = runtimeRaw || envRaw;

  if (!resolved) {
    return '/api/v1';
  }

  const base = resolved.replace(/\/$/, '');
  if (base === '/api' || base.endsWith('/api')) {
    return `${base}/v1`;
  }
  return `${base}/api/v1`;
}

export type ApiFetchInit = Omit<RequestInit, 'body' | 'credentials'> & {
  body?: unknown;
};

export async function apiFetch<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const url = `${apiV1Base()}${path.startsWith('/') ? path : `/${path}`}`;
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
    body: init.body === undefined ? undefined : init.body instanceof FormData ? init.body : JSON.stringify(init.body),
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  let json: unknown;
  if (text) {
    try {
      json = JSON.parse(text) as unknown;
    } catch {
      json = text;
    }
  }

  if (!res.ok) {
    const msg = parseApiErrorMessage(json, res.status);
    throw new ApiError(msg, res.status, json);
  }

  return json as T;
}

function parseApiErrorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    const detail = o.detail ?? o.message ?? o.error;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0 && typeof detail[0] === 'object') {
      const first = detail[0] as Record<string, unknown>;
      const msg = first.msg ?? first.message;
      if (typeof msg === 'string') return msg;
    }
  }
  if (typeof body === 'string' && body.trim()) return body;
  if (status === 401) return 'Неверный логин или пароль.';
  if (status === 403) return 'Доступ запрещён.';
  if (status === 404) return 'Ресурс не найден.';
  return 'Ошибка запроса.';
}
